import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  DataListItem,
  Highlight,
  Icon,
  RowsSkeletonLoader,
  Tooltip,
  type IconName,
} from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { EMBEDDED_CUSTOMER_STATUSES } from '../helpers/constants'
import { UnifiedAssociate } from '../helpers/types'

interface AssociateDetailsProps {
  associatesData: UnifiedAssociate[]
  isLoading: boolean
  isError: boolean
  refetch: () => void
  pendingVerificationStatus: boolean
}

const DOC_TYPE_ICON: Record<string, { icon: IconName; labelKey: string }> = {
  PASSPORT: { icon: 'Files', labelKey: 'associates.docType.passport' },
  ID_CARD: { icon: 'Files', labelKey: 'associates.docType.idCard' },
  DRIVER_LICENSE: { icon: 'Files', labelKey: 'associates.docType.driverLicense' },
  RESIDENCE_PERMIT: { icon: 'Files', labelKey: 'associates.docType.residencePermit' },
  SELFIE: { icon: 'UserCircle', labelKey: 'associates.docType.selfie' },
  PROOF_OF_ADDRESS: { icon: 'AddressBook', labelKey: 'associates.docType.proofOfAddress' },
  UTILITY_BILL: { icon: 'AddressBook', labelKey: 'associates.docType.utilityBill' },
  PROOF_OF_RESIDENCE: { icon: 'AddressBook', labelKey: 'associates.docType.proofOfResidence' },
}

const DEFAULT_DOC_ICON: IconName = 'Files'

export const AssociateDetails = ({
  associatesData,
  isLoading,
  isError,
  refetch,
  pendingVerificationStatus,
}: AssociateDetailsProps) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (isError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  return (
    <Box>
      <h4 className="text-h4 text-foreground-prominent">{t('associates.description')}</h4>
      {pendingVerificationStatus && (
        <Box className="mt-4">
          <p className="text-body-small text-foreground-prominent mt-16">
            {t('associates.pendingVerification')}
          </p>
        </Box>
      )}
      <Box flexCol paddingY={16}>
        {associatesData?.map(associate => {
          const isEmbeddedCustomerPending = associate?.status === EMBEDDED_CUSTOMER_STATUSES.PENDING
          return (
            <Box key={associate?.id} flexCol className="gap-1">
              <DataListItem
                id={associate?.id}
                title={associate?.title}
                body={associate?.body}
                layout="vertical"
                HeaderHighlight={
                  <Highlight type={isEmbeddedCustomerPending ? 'warning' : 'success'}>
                    {isEmbeddedCustomerPending
                      ? t('embeddedCustomers.status.infoRequired')
                      : t('embeddedCustomers.status.verified')}
                  </Highlight>
                }
              />
              {isEmbeddedCustomerPending && !!associate?.documents?.length && (
                <Box className="flex flex-row gap-3 pb-2 justify-end">
                  {associate.documents.map(doc => {
                    const mapping = DOC_TYPE_ICON[doc.docSetType]
                    const iconName = mapping?.icon ?? DEFAULT_DOC_ICON
                    const label = mapping
                      ? t(mapping.labelKey, { defaultValue: doc.docSetType })
                      : doc.docSetType
                    return (
                      <Tooltip key={doc.docSetType} bodyContent={label}>
                        <Box className="text-foreground-subdued hover:text-foreground-prominent transition-colors cursor-default">
                          <Icon name={iconName} size="medium" />
                        </Box>
                      </Tooltip>
                    )
                  })}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
